// Hợp đồng API của màn «Làm thumbnail» (Zod schema + kiểu response).
import { z } from 'zod';
/**
 * LÀM THUMBNAIL (06/10/2026, chủ dự án: «cover lại thumb của đối thủ thay đổi nền và text xuất ra ảnh JPG chất
 * lượng 4K tỷ lệ 16:9» · «Thiết kế thum của mình (ảnh nền, nhân vật png, text) xuất ra file ảnh JPG chất lượng
 * 4K tỷ lệ 16:9»).
 *
 * Bản thiết kế là một khối JSON vẽ bằng canvas ngay trong trình duyệt: mọi toạ độ tính trên KHUNG LÀM VIỆC
 * 1280×720; xuất 4K là vẽ lại cùng khối ấy với tỉ lệ 3 (3840×2160). Ảnh (nền, nhân vật PNG) gửi lên máy chủ
 * thành «tài sản» (`thumbnail_assets`), bản thiết kế chỉ giữ mã tài sản; thumbnail đối thủ thì lấy thẳng từ
 * `i.ytimg.com` (máy chủ ảnh của YouTube cho CORS, canvas không bị «nhiễm»).
 */
export const THUMB_WIDTH = 1280;
export const THUMB_HEIGHT = 720;
export const THUMB_EXPORT_SIZES = {
    '4k': { scale: 3, label: '4K · 3840×2160' },
    fhd: { scale: 1.5, label: 'Full HD · 1920×1080' },
    hd: { scale: 1, label: 'HD · 1280×720' },
};
/** Cỡ xuất mặc định (chủ dự án 06/10/2026: «chất lượng ảnh 1920 x 1080») — vừa dưới 2 MB của YouTube. */
export const DEFAULT_THUMB_EXPORT_SIZE = 'fhd';
/** YouTube chỉ nhận ảnh bìa tới 2 MB — xuất 4K dễ vượt, giao diện báo để hạ chất lượng. */
export const YOUTUBE_THUMBNAIL_LIMIT_BYTES = 2 * 1024 * 1024;
export const THUMB_ASSET_MAX_BYTES = 8 * 1024 * 1024;
export const THUMB_ASSET_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const THUMB_LAYERS_MAX = 30;
export const THUMB_TEXT_MAX = 300;
/** Số câu thay thế tối đa của một lớp chữ (31/10/2026). */
export const THUMB_VARIANTS_MAX = 12;
export const THUMB_NAME_MAX = 120;
export const THUMB_FONTS = [
    { family: 'Anton', label: 'Anton — đậm, hẹp', weights: [400], italic: false },
    // Chủ dự án 10/10/2026 cho «Thumb video ngắn»: bản đậm đúng tên (một độ đậm, nạp riêng ở `extra-fonts.ts`) + font
    // SVN / UTM lấy từ máy công ty (đủ dấu tiếng Việt, tự lưu trữ trong `font-files/`).
    { family: 'League Spartan Bold', label: 'League Spartan Bold', weights: [700], italic: false },
    {
        family: 'Montserrat ExtraBold',
        label: 'Montserrat ExtraBold',
        weights: [800],
        italic: false,
    },
    { family: 'UTM Bebas', label: 'UTM Bebas — cao, hẹp', weights: [400], italic: false },
    {
        family: 'Be Vietnam Pro ExtraBold',
        label: 'Be Vietnam Pro ExtraBold',
        weights: [800],
        italic: false,
    },
    {
        family: 'UTM Aquarelle',
        label: 'UTM Aquarelle (SVN-Aquarelle) — viết tay',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'UTM ViceroyJF',
        label: 'UTM ViceroyJF — viết tay',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'SVN-Snell Roundhand',
        label: 'SVN-Snell Roundhand — viết tay',
        weights: [400, 700],
        italic: false,
        script: true,
    },
    { family: 'Oswald', label: 'Oswald — hẹp', weights: [400, 700], italic: false },
    { family: 'Bangers', label: 'Bangers — truyện tranh', weights: [400], italic: false },
    { family: 'Bungee', label: 'Bungee — khối', weights: [400], italic: false },
    { family: 'Paytone One', label: 'Paytone One — tròn đậm', weights: [400], italic: false },
    { family: 'Baloo 2', label: 'Baloo 2 — tròn', weights: [400, 800], italic: false },
    { family: 'Kanit', label: 'Kanit', weights: [400, 700, 900], italic: true },
    { family: 'Exo 2', label: 'Exo 2', weights: [400, 700, 900], italic: true },
    { family: 'Montserrat', label: 'Montserrat', weights: [400, 700, 900], italic: true },
    { family: 'Be Vietnam Pro', label: 'Be Vietnam Pro', weights: [400, 700, 900], italic: true },
    { family: 'Roboto Condensed', label: 'Roboto Condensed', weights: [400, 700], italic: true },
    {
        family: 'Playfair Display',
        label: 'Playfair Display — có chân',
        weights: [400, 700, 900],
        italic: true,
    },
    { family: 'Mitr', label: 'Mitr', weights: [400, 700], italic: false },
    {
        family: 'Lobster',
        label: 'Lobster — viết tay đậm',
        weights: [400],
        italic: false,
        script: true,
    },
    { family: 'Pacifico', label: 'Pacifico — viết tay', weights: [400], italic: false, script: true },
    { family: 'Pattaya', label: 'Pattaya — viết tay', weights: [400], italic: false, script: true },
    {
        family: 'Dancing Script',
        label: 'Dancing Script — viết tay',
        weights: [400, 700],
        italic: false,
        script: true,
    },
    { family: 'Charm', label: 'Charm — viết tay', weights: [400, 700], italic: false, script: true },
    { family: 'Itim', label: 'Itim — viết tay mềm', weights: [400], italic: false },
    { family: 'Sriracha', label: 'Sriracha — viết tay', weights: [400], italic: false },
    // Đợt thêm 06/10/2026 (chủ dự án: «thêm nhiều font chữ đẹp») — vẫn chỉ font có bộ chữ tiếng Việt.
    { family: 'Bungee Shade', label: 'Bungee Shade — khối đổ bóng', weights: [400], italic: false },
    {
        family: 'Bungee Inline',
        label: 'Bungee Inline — khối kẻ trong',
        weights: [400],
        italic: false,
    },
    {
        family: 'Alfa Slab One',
        label: 'Alfa Slab One — đậm chân vuông',
        weights: [400],
        italic: false,
    },
    { family: 'Bevan', label: 'Bevan — đậm chân vuông', weights: [400], italic: true },
    { family: 'Chonburi', label: 'Chonburi — có chân, bề thế', weights: [400], italic: false },
    { family: 'Yeseva One', label: 'Yeseva One — có chân, sang', weights: [400], italic: false },
    {
        family: 'Roboto Slab',
        label: 'Roboto Slab — chân vuông',
        weights: [400, 700, 900],
        italic: false,
    },
    {
        family: 'Saira Extra Condensed',
        label: 'Saira Extra Condensed — cao, hẹp',
        weights: [400, 700, 900],
        italic: false,
    },
    { family: 'Prompt', label: 'Prompt — đậm hiện đại', weights: [400, 700, 900], italic: true },
    { family: 'Lexend', label: 'Lexend — rõ, hiện đại', weights: [400, 700, 900], italic: false },
    { family: 'Nunito', label: 'Nunito — tròn, thân thiện', weights: [400, 700, 900], italic: true },
    { family: 'Quicksand', label: 'Quicksand — tròn, nhẹ', weights: [400, 700], italic: false },
    {
        family: 'Josefin Sans',
        label: 'Josefin Sans — thanh, cổ điển',
        weights: [400, 700],
        italic: true,
    },
    {
        family: 'Chakra Petch',
        label: 'Chakra Petch — góc cạnh, công nghệ',
        weights: [400, 700],
        italic: true,
    },
    { family: 'Philosopher', label: 'Philosopher — nét mềm', weights: [400, 700], italic: true },
    {
        family: 'Cormorant',
        label: 'Cormorant — có chân, thanh lịch',
        weights: [400, 700],
        italic: true,
    },
    { family: 'Vollkorn', label: 'Vollkorn — có chân, ấm', weights: [400, 700, 900], italic: true },
    {
        family: 'Taviraj',
        label: 'Taviraj — có chân, nét mảnh',
        weights: [400, 700, 900],
        italic: true,
    },
    { family: 'Arima', label: 'Arima — mềm, bay', weights: [400, 700], italic: false },
    { family: 'Athiti', label: 'Athiti — gọn', weights: [400, 700], italic: false },
    { family: 'Mali', label: 'Mali — viết tay tròn', weights: [400, 700], italic: true },
    {
        family: 'Charmonman',
        label: 'Charmonman — viết tay bay',
        weights: [400, 700],
        italic: false,
        script: true,
    },
    {
        family: 'Srisakdi',
        label: 'Srisakdi — viết tay cổ',
        weights: [400, 700],
        italic: false,
        script: true,
    },
    // 10/10/2026 «add nhiều font chữ đẹp»: thêm 16 font viết tay + 10 font chữ in / có chân, đều có bộ chữ tiếng Việt.
    {
        family: 'Great Vibes',
        label: 'Great Vibes — viết tay mềm mại',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Alex Brush',
        label: 'Alex Brush — viết tay bút lông',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Allura',
        label: 'Allura — viết tay thanh',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Playball',
        label: 'Playball — viết tay đậm, khoẻ',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Style Script',
        label: 'Style Script — viết tay đậm',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Comforter Brush',
        label: 'Comforter Brush — cọ vẽ',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Kolker Brush',
        label: 'Kolker Brush — cọ vẽ',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Pinyon Script',
        label: 'Pinyon Script — viết tay cổ điển',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Imperial Script',
        label: 'Imperial Script — viết tay sang',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Birthstone Bounce',
        label: 'Birthstone Bounce — viết tay bay',
        weights: [400, 500],
        italic: false,
        script: true,
    },
    {
        family: 'Meow Script',
        label: 'Meow Script — viết tay tròn',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Vujahday Script',
        label: 'Vujahday Script — viết tay nghệ',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Lavishly Yours',
        label: 'Lavishly Yours — viết tay hoa mỹ',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Water Brush',
        label: 'Water Brush — cọ nước',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Ephesis',
        label: 'Ephesis — viết tay nhẹ',
        weights: [400],
        italic: false,
        script: true,
    },
    {
        family: 'Corinthia',
        label: 'Corinthia — viết tay thanh lịch',
        weights: [400, 700],
        italic: false,
        script: true,
    },
    {
        family: 'Barlow Condensed',
        label: 'Barlow Condensed — hẹp, đậm',
        weights: [700, 900],
        italic: false,
    },
    {
        family: 'Encode Sans Condensed',
        label: 'Encode Sans Condensed — hẹp, đậm',
        weights: [800, 900],
        italic: false,
    },
    {
        family: 'Montserrat Alternates',
        label: 'Montserrat Alternates — tròn hiện đại',
        weights: [700, 900],
        italic: false,
    },
    {
        family: 'League Spartan',
        label: 'League Spartan — đậm hình học',
        weights: [700, 900],
        italic: false,
    },
    { family: 'Archivo', label: 'Archivo — đậm chắc', weights: [800, 900], italic: false },
    { family: 'Raleway', label: 'Raleway — thanh lịch', weights: [700, 900], italic: false },
    {
        family: 'Noto Serif Display',
        label: 'Noto Serif Display — có chân, sang',
        weights: [700, 900],
        italic: false,
    },
    { family: 'Fraunces', label: 'Fraunces — có chân mềm', weights: [700, 900], italic: false },
    { family: 'Lora', label: 'Lora — có chân, ấm', weights: [700], italic: false },
    { family: 'Prata', label: 'Prata — có chân, cổ điển', weights: [400], italic: false },
];
export const THUMB_FONT_FAMILIES = THUMB_FONTS.map((font) => font.family);
/** Nguồn ảnh ngoài duy nhất được phép ghi vào bản thiết kế: thumbnail trên máy chủ ảnh của YouTube. */
export const YOUTUBE_IMAGE_ORIGIN = 'https://i.ytimg.com/';
export function youtubeThumbnailUrl(videoId, quality = 'maxresdefault') {
    return `${YOUTUBE_IMAGE_ORIGIN}vi/${videoId}/${quality}.jpg`;
}
const color = z.string().trim().min(1).max(60);
const coord = z.number().min(-10000).max(10000);
const unit = z.number().min(0).max(1);
const assetId = z.string().max(40).default('');
/** Ảnh ngoài: '' (không có) hoặc đúng máy chủ ảnh YouTube. `blob:`/`data:` chỉ sống trong trình duyệt, không ghi. */
const imageSrc = z
    .string()
    .max(500)
    .default('')
    .refine((value) => value === '' || value.startsWith(YOUTUBE_IMAGE_ORIGIN), {
    message: 'Ảnh ngoài chỉ nhận từ i.ytimg.com — ảnh khác thì tải lên thành tài sản',
});
export const thumbShadowSchema = z.object({
    color,
    blur: z.number().min(0).max(200),
    offsetX: z.number().min(-200).max(200),
    offsetY: z.number().min(-200).max(200),
});
export const thumbStrokeSchema = z.object({ color, width: z.number().min(0).max(100) });
const layerBase = {
    id: z.string().min(1).max(40),
    name: z.string().max(60).default(''),
    /** Góc trên-trái của lớp trên khung 1280×720. */
    x: coord,
    y: coord,
    /** Độ, quay quanh tâm lớp. */
    rotation: z.number().min(-360).max(360).default(0),
    opacity: unit.default(1),
    hidden: z.boolean().default(false),
    locked: z.boolean().default(false),
};
export const thumbTextFillSchema = z.object({
    kind: z.enum(['solid', 'gradient']).default('solid'),
    color,
    /** Màu cuối khi tô chuyển sắc. */
    to: color.default('#ffffff'),
    /** Hướng chuyển sắc, độ: 90 = trên xuống dưới. */
    angle: z.number().min(0).max(360).default(90),
});
export const THUMB_PLATE_KINDS = ['none', 'pill', 'rect'];
export const thumbTextPlateSchema = z.object({
    kind: z.enum(THUMB_PLATE_KINDS).default('none'),
    color: color.default('#ffffff'),
    borderColor: color.default('#000000'),
    borderWidth: z.number().min(0).max(40).default(0),
    paddingX: z.number().min(0).max(300).default(24),
    paddingY: z.number().min(0).max(300).default(8),
    radius: z.number().min(0).max(300).default(12),
});
export const thumbTextLayerSchema = z.object({
    kind: z.literal('text'),
    ...layerBase,
    text: z.string().max(THUMB_TEXT_MAX),
    /**
     * CÂU THAY THẾ (31/10/2026, chủ dự án: «mỗi text có 4,5 câu thay thế nhau, bốc random»): «Tạo hàng loạt» và «Bốc
     * ngẫu nhiên» bốc ngẫu nhiên một câu trong (câu đang hiện + các câu này) cho từng ảnh; màn soạn vẫn hiện `text`.
     * Mỗi phần tử một câu (ô nhập mỗi dòng một câu; dòng trống / trùng bỏ qua khi bốc). Thiếu = không có.
     */
    variants: z.array(z.string().max(THUMB_TEXT_MAX)).max(THUMB_VARIANTS_MAX).optional(),
    font: z.string().max(60),
    weight: z.number().int().min(100).max(900).default(700),
    italic: z.boolean().default(false),
    /** Cỡ chữ (px trên khung 1280×720). */
    size: z.number().min(8).max(600),
    letterSpacing: z.number().min(-20).max(100).default(0),
    lineHeight: z.number().min(0.6).max(3).default(1.15),
    align: z.enum(['left', 'center', 'right']).default('left'),
    uppercase: z.boolean().default(false),
    fill: thumbTextFillSchema,
    stroke: thumbStrokeSchema,
    /** Viền ngoài (viền kép, 06/10/2026 «thêm option viền»): vẽ rộng hơn viền trong, nằm phía ngoài. */
    stroke2: thumbStrokeSchema.default({ color: '#000000', width: 0 }),
    shadow: thumbShadowSchema,
    /** Khối nổi 3D: vẽ chữ lặp dần xuống dưới-phải `depth` px bằng màu này. */
    extrude: z.object({ depth: z.number().min(0).max(60), color }),
    /**
     * NỀN CHỮ (31/10/2026, mẫu Bolero của chủ dự án: «KHÔNG QUẢNG CÁO» nằm trong viên thuốc trắng viền đen): hộp
     * sau khối chữ, rộng hơn chữ mỗi bên `paddingX` / `paddingY`; `pill` bo tròn hết cỡ, `rect` bo theo `radius`.
     * Thiếu (bản cũ) = không có nền.
     */
    plate: thumbTextPlateSchema.optional(),
});
export const thumbImageLayerSchema = z.object({
    kind: z.literal('image'),
    ...layerBase,
    assetId,
    src: imageSrc,
    w: z.number().min(1).max(20000),
    h: z.number().min(1).max(20000),
    flipX: z.boolean().default(false),
    /** Viền quanh hình (nhân vật PNG tách nền) — vẽ bằng bóng của chính ảnh. */
    stroke: thumbStrokeSchema,
    shadow: thumbShadowSchema,
});
/**
 * LỚP ĐƯỜNG KẺ TRANG TRÍ (31/10/2026, mẫu Bolero: vạch vàng sáng dưới «SIÊU HAY», nét lượn trắng → hồng dưới «CỰC ÊM
 * TAI»; bản đầu gắn «gạch dưới» vào lớp chữ, chủ dự án: «không điều chỉnh được» → thành lớp riêng, bấm chọn / kéo /
 * co giãn / xoá như lớp ảnh). Vẽ trong hộp `w`×`h`: `line` = vạch ngang giữa hộp, mờ dần hai đầu theo `fade`;
 * `swoosh` = nét cong từ góc dưới-trái vút lên góc trên-phải, thon dần về mũi. Màu chuyển `color` → `to` theo chiều
 * dài; `glow` = quầng sáng cùng màu cuối.
 */
export const THUMB_SHAPE_KINDS = ['line', 'swoosh'];
export const thumbShapeLayerSchema = z.object({
    kind: z.literal('shape'),
    ...layerBase,
    shape: z.enum(THUMB_SHAPE_KINDS),
    w: z.number().min(1).max(20000),
    h: z.number().min(1).max(20000),
    color,
    to: color.default('#ffffff'),
    /** Bề dày nét (px trên khung 1280×720). */
    width: z.number().min(1).max(80).default(4),
    glow: z.number().min(0).max(100).default(0),
    /** Phần chiều dài mờ dần ở mỗi đầu vạch (0 = hai đầu rõ). */
    fade: z.number().min(0).max(0.5).default(0.1),
});
export const thumbLayerSchema = z.discriminatedUnion('kind', [
    thumbTextLayerSchema,
    thumbImageLayerSchema,
    thumbShapeLayerSchema,
]);
export const thumbBackgroundSchema = z.object({
    kind: z.enum(['color', 'image']).default('color'),
    color: color.default('#0b3a36'),
    /** Khác rỗng = nền chuyển sắc từ `color` sang màu này. */
    gradientTo: z.string().trim().max(60).default(''),
    gradientAngle: z.number().min(0).max(360).default(180),
    assetId,
    src: imageSrc,
    /** Phóng ảnh nền (1 = vừa phủ khung), dịch ảnh theo phần của khoảng dư (-1..1). */
    zoom: z.number().min(1).max(4).default(1),
    offsetX: z.number().min(-1).max(1).default(0),
    offsetY: z.number().min(-1).max(1).default(0),
    blur: z.number().min(0).max(60).default(0),
    darken: unit.default(0),
});
/** Viền khung quanh cả thumbnail (06/10/2026 «thêm option viền»): độ dày px trên khung 1280×720. */
export const thumbFrameSchema = z.object({ width: z.number().min(0).max(80), color });
/**
 * DẠNG ẢNH (10/10/2026, chủ dự án: «Tôi muốn làm ảnh avata kênh, ảnh bìa kênh, bạn thiết kế chức năng này»): cùng
 * một màn soạn, đổi khung làm việc theo dạng. Ảnh bìa YouTube 2560×1440 cùng tỉ lệ 16:9 với thumbnail nên dùng chung
 * khung 1280×720 (xuất nhân 2); ảnh đại diện khung vuông 800×800.
 */
export const THUMB_FORMATS = ['thumb', 'banner', 'avatar'];
export const THUMB_FORMAT_INFO = {
    thumb: { label: 'Thumbnail', width: THUMB_WIDTH, height: THUMB_HEIGHT },
    banner: { label: 'Ảnh bìa kênh', width: THUMB_WIDTH, height: THUMB_HEIGHT },
    avatar: { label: 'Ảnh đại diện', width: 800, height: 800 },
};
/** Khung làm việc của một bản thiết kế. */
export function designSize(design) {
    const info = THUMB_FORMAT_INFO[design.format ?? 'thumb'];
    return { width: info.width, height: info.height };
}
export const YOUTUBE_BANNER_LIMIT_BYTES = 6 * 1024 * 1024;
export const YOUTUBE_AVATAR_LIMIT_BYTES = 4 * 1024 * 1024;
/**
 * Vùng ảnh bìa (trên khung 1280×720 = một nửa 2560×1440), theo hướng dẫn YouTube: TV thấy cả ảnh; máy tính thấy dải
 * giữa cao 423 px (bản gốc); điện thoại / mọi thiết bị chỉ chắc chắn thấy VÙNG AN TOÀN 1546×423 giữa ảnh — chữ, logo
 * đặt trong vùng này.
 */
export function exportOptionsFor(format) {
    if (format === 'banner') {
        return [
            {
                key: 'banner',
                label: '2560×1440 · ảnh bìa YouTube',
                scale: 2,
                type: 'image/jpeg',
                limitBytes: YOUTUBE_BANNER_LIMIT_BYTES,
            },
        ];
    }
    if (format === 'avatar') {
        return [
            {
                key: 'avatar-png',
                label: '800×800 · PNG',
                scale: 1,
                type: 'image/png',
                limitBytes: YOUTUBE_AVATAR_LIMIT_BYTES,
            },
            {
                key: 'avatar-jpg',
                label: '800×800 · JPG',
                scale: 1,
                type: 'image/jpeg',
                limitBytes: YOUTUBE_AVATAR_LIMIT_BYTES,
            },
        ];
    }
    return Object.keys(THUMB_EXPORT_SIZES).map((key) => ({
        key,
        label: THUMB_EXPORT_SIZES[key].label,
        scale: THUMB_EXPORT_SIZES[key].scale,
        type: 'image/jpeg',
        limitBytes: YOUTUBE_THUMBNAIL_LIMIT_BYTES,
    }));
}
export const BANNER_DESKTOP_AREA = { x: 0, y: 254.25, w: THUMB_WIDTH, h: 211.5 };
export const BANNER_SAFE_AREA = { x: 253.5, y: 254.25, w: 773, h: 211.5 };
export const thumbDesignSchema = z.object({
    version: z.literal(1),
    format: z.enum(THUMB_FORMATS).default('thumb'),
    background: thumbBackgroundSchema,
    frame: thumbFrameSchema.default({ width: 0, color: '#ffffff' }),
    layers: z.array(thumbLayerSchema).max(THUMB_LAYERS_MAX),
});
export const thumbDesignSaveSchema = z.object({
    name: z.string().trim().min(1).max(THUMB_NAME_MAX),
    design: thumbDesignSchema,
    /** Ảnh xem trước nhỏ (JPG 320×180) đã tải lên thành tài sản — hiện ở danh sách bản thiết kế. */
    previewAssetId: assetId,
    /**
     * Nằm ở «thư mục» Mẫu đã chỉnh sửa (06/10/2026, chủ dự án: «file đã chỉnh sửa lưu tôi muốn bạn cho vào file
     * Lưu các mẫu đã chỉnh sửa») — mẫu cả bộ phận dùng lại; không có thì là bản thiết kế thường.
     */
    isTemplate: z.boolean().default(false),
});
/** Chuyển một bản vào / ra thư mục Mẫu đã chỉnh sửa, không đụng nội dung. */
export const thumbDesignTemplateSchema = z.object({ isTemplate: z.boolean() });
export const thumbDesignParamsSchema = z.object({ id: z.string().min(1).max(40) });
export const thumbAssetParamsSchema = z.object({ id: z.string().min(1).max(40) });
export const thumbCompetitorQuerySchema = z.object({
    q: z.string().trim().max(100).default(''),
    limit: z.coerce.number().int().min(1).max(60).default(40),
});
/** Đường tải một tài sản (ảnh) — web ghép sau `/api/v1`. */
export function thumbAssetPath(id) {
    return `/thumbnail-maker/assets/${encodeURIComponent(id)}`;
}
/** Mọi mã tài sản một bản thiết kế đang dùng (nền + các lớp ảnh), không trùng. */
export function thumbAssetIds(design) {
    const ids = new Set();
    if (design.background.kind === 'image' && design.background.assetId) {
        ids.add(design.background.assetId);
    }
    for (const layer of design.layers) {
        if (layer.kind === 'image' && layer.assetId)
            ids.add(layer.assetId);
    }
    return [...ids];
}
/*
 * ------------------------------------------------------------------------------------------------------------
 * MẪU «THUMB VIDEO NGẮN» CHỈNH ĐƯỢC (10/10/2026, chủ dự án: «Khi chỉnh sửa mẫu 01.02.03 thì khi sửa xong sẽ lưu đè
 * lên mẫu đó, không được nhảy sang phần làm thumbnail»). Phần chỉnh đè lên mẫu gốc trong mã, lưu chung cả công ty.
 * ------------------------------------------------------------------------------------------------------------
 */
export const QUICK_TEMPLATE_IDS = ['xom-dao', 'di-vang', 'bay-dem'];
const offset = z.number().min(-1500).max(1500);
const scaleFactor = z.number().min(0.3).max(3);
const percent = z.number().min(50).max(160);
/** Chỉnh MỘT dòng chữ (khoá = tên dòng: «Tên bài (viết tay 1)», «Ca sĩ»…): dời, cỡ, font, màu. */
export const QUICK_TEXT_CASES = ['upper', 'lower', 'title'];
export const quickLineStyleSchema = z.object({
    dx: offset.default(0),
    dy: offset.default(0),
    scale: scaleFactor.default(1),
    font: z.string().trim().max(60).optional(),
    fill: thumbTextFillSchema.optional(),
    stroke: thumbStrokeSchema.optional(),
    /**
     * Chỉnh thêm từng dòng (10/10/2026, chủ dự án: «Chữ thường, Chữ Hoa, co chữ, giãn chữ, viền bóng»): kiểu chữ
     * (CHỮ HOA · chữ thường · Hoa Đầu Từ), khoảng cách chữ (âm = co, dương = giãn), bóng chữ. Thiếu = như mẫu.
     */
    textCase: z.enum(QUICK_TEXT_CASES).optional(),
    letterSpacing: z.number().min(-20).max(60).optional(),
    shadow: thumbShadowSchema.optional(),
});
export const quickTemplateSettingsSchema = z.object({
    /** Font theo vai (chữ in tên bài / viết tay / ca sĩ); thiếu = như mẫu. */
    fonts: z
        .object({
        print: z.string().trim().max(60).optional(),
        script: z.string().trim().max(60).optional(),
        singer: z.string().trim().max(60).optional(),
    })
        .default({}),
    /** Cỡ chữ theo vai, phần trăm. */
    scale: z
        .object({ print: percent, script: percent, singer: percent })
        .default({ print: 100, script: 100, singer: 100 }),
    /** Dời cả khối chữ. */
    offsetX: offset.default(0),
    offsetY: offset.default(0),
    lines: z
        .record(z.string().trim().min(1).max(60), quickLineStyleSchema)
        .refine((lines) => Object.keys(lines).length <= 30, 'Quá nhiều dòng')
        .default({}),
    /** Nhân vật PNG: dời + phóng. */
    character: z
        .object({ dx: offset.default(0), dy: offset.default(0), scale: scaleFactor.default(1) })
        .default({ dx: 0, dy: 0, scale: 1 }),
    /**
     * Logo (10/10/2026, chủ dự án: «thêm ảnh logo»): ảnh trong kho ảnh thumbnail (`thumbnail_assets`, không bị dọn
     * khi mẫu còn dùng), góc trên trái + bề rộng trên khung 1280×720, `ratio` = rộng / cao của ảnh.
     */
    logo: z
        .object({
        assetId: z.string().min(1).max(40),
        ratio: z.number().min(0.05).max(20),
        x: offset,
        y: offset,
        width: z.number().min(20).max(THUMB_WIDTH),
    })
        .nullable()
        .default(null),
});
export const quickTemplateParamsSchema = z.object({ id: z.enum(QUICK_TEMPLATE_IDS) });
//# sourceMappingURL=thumbnail-maker.js.map